import { createSlice } from "@reduxjs/toolkit";
import { getValidStoredUser, isTokenValid } from "../utils/authHelper";

const storedUser = getValidStoredUser();

const initialState = {
  userInfo: storedUser,
  isAuthenticated: !!(storedUser && storedUser.token && isTokenValid(storedUser.token)),
  hasAcceptedTerms: !!storedUser?.termsAccepted,
  sessionConflict: null,
  sessionExpired: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLogin: (state, action) => {
      const payload = action.payload || {};
      const payloadId = payload.user_id || payload.id;
      const existing = state.userInfo || getValidStoredUser() || {};
      const existingId = existing.user_id || existing.id;

      // If this is a fresh login with a token or a different user, start fresh
      const isFreshOrDifferentUser = payload.isFreshLogin || (payload.token && payloadId && payloadId !== existingId);

      const updated = isFreshOrDifferentUser
        ? {
            ...payload,
            token: payload.token,
            id: payloadId,
            user_id: payloadId,
            role: payload.role,
            name: payload.name || payloadId,
            email: payload.email,
            phone: payload.phone,
            termsAccepted: payload.termsAccepted !== undefined ? payload.termsAccepted : true,
          }
        : {
            ...existing,
            ...payload,
            token: payload.token || existing.token,
            id: payloadId || existingId,
            user_id: payloadId || existingId,
            role: payload.role || existing.role,
            name: payload.name || existing.name || payloadId || existingId,
            termsAccepted:
              payload.termsAccepted !== undefined
                ? payload.termsAccepted
                : existing.termsAccepted !== undefined
                ? existing.termsAccepted
                : true,
          };

      state.userInfo = updated;
      state.isAuthenticated = !!(updated.token && isTokenValid(updated.token));
      state.hasAcceptedTerms = !!updated.termsAccepted;
      state.sessionExpired = null;
      localStorage.setItem("userInfo", JSON.stringify(updated));
      // Fresh login immediately starts an active session timestamp
      localStorage.setItem("quickpay_session_last_active", Date.now().toString());
    },
    setLogout: (state) => {
      state.userInfo = null;
      state.isAuthenticated = false;
      state.hasAcceptedTerms = false;
      state.sessionConflict = null;
      localStorage.removeItem("userInfo");
      localStorage.removeItem("quickpay_session_last_active");
    },
    updateSessionToken: (state, action) => {
      const newToken = action.payload?.token;
      if (newToken && state.userInfo) {
        state.userInfo.token = newToken;
        state.isAuthenticated = isTokenValid(newToken);
        try {
          const stored = JSON.parse(localStorage.getItem("userInfo") || "{}");
          stored.token = newToken;
          localStorage.setItem("userInfo", JSON.stringify(stored));
        } catch (e) {}
      }
    },
    setSessionExpired: (state, action) => {
      state.sessionExpired = action.payload || true;
      state.userInfo = null;
      state.isAuthenticated = false;
      state.hasAcceptedTerms = false;
      state.sessionConflict = null;
      localStorage.removeItem("userInfo");
      localStorage.removeItem("quickpay_session_last_active");
    },
    clearSessionExpired: (state) => {
      state.sessionExpired = null;
    },
    setShowSessionConflict: (state, action) => {
      state.sessionConflict = action.payload;
    },
    clearSessionConflict: (state) => {
      state.sessionConflict = null;
    },
  },
});

export const {
  setLogin,
  setLogout,
  updateSessionToken,
  setSessionExpired,
  clearSessionExpired,
  setShowSessionConflict,
  clearSessionConflict,
} = authSlice.actions;

export default authSlice.reducer;